import { fetchData, patchData, postData } from '../client';
import { Pagination } from '@/types/common.types';
import {
  Group,
  Message,
  MessageThread,
  ApiResponse,
  PaginationParams,
  SyncResponse,
  ThreadsResponse,
  GetGroupsResponse,
  GetMessagesResponse,
} from '@/types/groups';
import { MediaWithPresignedUrl, MediaResponse } from '@/types/media';

interface ThreadMessagesResponse {
  messages: Message[];
  pagination?: Pagination;
}

export const groupsApi = {
  getGroups: async (params?: PaginationParams): Promise<GetGroupsResponse> => {
    return fetchData<GetGroupsResponse>({
      path: '/api/groups',
      params: params
        ? {
            ...(params.limit && { limit: params.limit.toString() }),
            ...(params.page && { page: params.page.toString() }),
            ...(params.search && { search: params.search }),
          }
        : undefined,
    });
  },

  getGroupById: async (groupId: string): Promise<ApiResponse<Group>> => {
    return fetchData<ApiResponse<Group>>({
      path: `/api/groups/${groupId}`,
    });
  },

  getGroupThreads: async (
    groupId: string,
    params?: PaginationParams,
  ): Promise<ApiResponse<ThreadsResponse>> => {
    return fetchData<ApiResponse<ThreadsResponse>>({
      path: `/api/groups/${groupId}/threads`,
      params: params
        ? {
            ...(params.limit && { limit: params.limit.toString() }),
            ...(params.page && { page: params.page.toString() }),
          }
        : undefined,
    });
  },

  getGroupMessages: async (
    groupId: string,
    params?: PaginationParams & { threadId?: string; sort?: string },
  ): Promise<GetMessagesResponse> => {
    return fetchData<GetMessagesResponse>({
      path: `/api/groups/${groupId}/messages`,
      params: params
        ? {
            ...(params.limit && { limit: params.limit.toString() }),
            ...(params.page && { page: params.page.toString() }),
            ...(params.threadId && { threadId: params.threadId }),
            ...(params.sort && { sort: params.sort }),
          }
        : undefined,
    });
  },

  getGroupMedia: async (
    groupId: string,
    params?: PaginationParams & {
      mediaType?: string;
      threadId?: string;
      contactId?: string;
    },
  ): Promise<ApiResponse<MediaWithPresignedUrl[]>> => {
    return fetchData<ApiResponse<MediaWithPresignedUrl[]>>({
      path: `/api/groups/${groupId}/media`,
      params: params
        ? {
            ...(params.limit && { limit: params.limit.toString() }),
            ...(params.page && { page: params.page.toString() }),
            ...(params.mediaType && { mediaType: params.mediaType }),
            ...(params.threadId && { threadId: params.threadId }),
            ...(params.contactId && {
              contactId: params.contactId,
            }),
          }
        : undefined,
    });
  },

  getThreadMessages: async (
    threadId: string,
    params?: PaginationParams,
  ): Promise<ApiResponse<ThreadMessagesResponse>> => {
    return fetchData<ApiResponse<ThreadMessagesResponse>>({
      path: `/api/threads/${threadId}/messages`,
      params: params
        ? {
            ...(params.limit && { limit: params.limit.toString() }),
            ...(params.page && { page: params.page.toString() }),
          }
        : undefined,
    });
  },

  getThreadMedia: async (
    threadId: string,
    params?: PaginationParams & { mediaType?: string; contactId?: string },
  ): Promise<ApiResponse<MediaWithPresignedUrl[]>> => {
    return fetchData<ApiResponse<MediaWithPresignedUrl[]>>({
      path: `/api/threads/${threadId}/media`,
      params: params
        ? {
            ...(params.limit && { limit: params.limit.toString() }),
            ...(params.page && { page: params.page.toString() }),
            ...(params.mediaType && { mediaType: params.mediaType }),
            ...(params.contactId && {
              contactId: params.contactId,
            }),
          }
        : undefined,
    });
  },

  regenerateThreadAnalysis: async (
    threadId: string,
  ): Promise<ApiResponse<{ thread: MessageThread }>> => {
    return postData<ApiResponse<{ thread: MessageThread }>>({
      path: `/api/threads/${threadId}/regenerate`,
    });
  },

  getAllMedia: async (
    params?: PaginationParams & {
      searchQuery?: string;
      mediaTypes?: string[];
      groupIds?: string[];
      threadId?: string;
      contactIds?: string[];
      tags?: string[];
      timeFrame?: string;
    },
  ): Promise<ApiResponse<MediaResponse>> => {
    return fetchData<ApiResponse<MediaResponse>>({
      path: '/api/media',
      params: params
        ? {
            ...(params.limit && { limit: params.limit.toString() }),
            ...(params.page && { page: params.page.toString() }),
            ...(params.searchQuery && { searchQuery: params.searchQuery }),
            ...((params.mediaTypes ?? []).length > 0 && {
              mediaTypes: params.mediaTypes?.join(','),
            }),
            ...((params.groupIds ?? []).length > 0 && {
              groupIds: params.groupIds?.join(','),
            }),
            ...(params.threadId && { threadId: params.threadId }),
            ...((params.contactIds ?? []).filter(Boolean).length > 0 && {
              contactIds: (params.contactIds ?? []).filter(Boolean).join(','),
            }),
            ...((params.tags ?? []).length > 0 && {
              tags: params.tags?.join(','),
            }),
            ...(params.timeFrame && { timeFrame: params.timeFrame }),
          }
        : undefined,
    });
  },

  getMediaById: async (
    mediaId: string,
  ): Promise<ApiResponse<MediaWithPresignedUrl>> => {
    return fetchData<ApiResponse<MediaWithPresignedUrl>>({
      path: `/api/media/${mediaId}`,
    });
  },

  syncGroups: async (): Promise<ApiResponse<SyncResponse>> => {
    return postData<ApiResponse<SyncResponse>>({
      path: '/api/groups/sync',
    });
  },

  getGroupUnreadMessageCount: async (): Promise<
    ApiResponse<Record<string, string>>
  > => {
    return fetchData<ApiResponse<Record<string, string>>>({
      path: '/api/groups/unread-message-count',
    });
  },

  updateMessageReadStatus: async (
    groupId: string,
  ): Promise<ApiResponse<void>> => {
    return patchData<ApiResponse<void>>({
      path: `/api/groups/${groupId}/update-read-status`,
    });
  },
};
